import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { t } from "@/i18n";
import { toast } from "sonner";
import { Trash2, ExternalLink, LogOut, Building2, Plus, ChevronDown, ChevronRight, Clock, LogIn, BarChart3, MapPin, StickyNote, X, Search, Paperclip, FileText, Image as ImageIcon } from "lucide-react";

const SUBSCRIPTION_META = {
  trial:         { label: "Testphase",        dot: "bg-[#C9A964]" },
  trial_expired: { label: "Testphase abgelaufen", dot: "bg-[#A66B62]" },
  active:        { label: "Aktiv",            dot: "bg-[#6B8E72]" },
  payment_open:  { label: "Zahlung offen",    dot: "bg-[#E0B96A]" },
  suspended:     { label: "Pausiert",         dot: "bg-[#A66B62]" },
  blocked:       { label: "Blockiert",        dot: "bg-[#C9514E]" },
};

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

function SubscriptionControl({ rest, onAction }) {
  const [open, setOpen] = useState(false);
  const meta = SUBSCRIPTION_META[rest.subscription_status] || SUBSCRIPTION_META.trial;
  const status = rest.subscription_status || "trial";
  const days = status === "trial" ? daysUntil(rest.trial_ends_at) : null;

  const items = [];
  if (status === "trial" || status === "trial_expired") {
    items.push({ key: "activate",          label: "Aktivieren",            tone: "primary" });
    items.push({ key: "extend_trial",      label: "Testphase +30 Tage" });
    items.push({ key: "mark_payment_open", label: "Zahlung offen markieren" });
    items.push({ key: "suspend",           label: "Deaktivieren",          tone: "danger" });
    items.push({ key: "block",             label: "Blockiert (Siehe Notiz)", tone: "danger" });
  }
  if (status === "active") {
    items.push({ key: "suspend",           label: "Deaktivieren",          tone: "danger" });
    items.push({ key: "mark_payment_open", label: "Zahlung offen markieren" });
    items.push({ key: "block",             label: "Blockiert (Siehe Notiz)", tone: "danger" });
  }
  if (status === "payment_open") {
    items.push({ key: "mark_paid",         label: "Als bezahlt markieren", tone: "primary" });
    items.push({ key: "suspend",           label: "Deaktivieren",          tone: "danger" });
    items.push({ key: "block",             label: "Blockiert (Siehe Notiz)", tone: "danger" });
  }
  if (status === "suspended") {
    items.push({ key: "reactivate",        label: "Aktivieren",            tone: "primary" });
    items.push({ key: "block",             label: "Blockiert (Siehe Notiz)", tone: "danger" });
  }
  if (status === "blocked") {
    items.push({ key: "reactivate",        label: "Entsperren / Aktivieren", tone: "primary" });
    items.push({ key: "suspend",           label: "Deaktivieren",          tone: "danger" });
  }

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`subscription-badge-${rest.restaurant_id}`}
        title="Abo-Status bearbeiten"
        className="inline-flex items-center gap-2 text-[11px] px-2 py-1 -mx-2 -my-1 rounded hover:bg-white/[0.04] transition-colors"
      >
        <span className={`w-2 h-2 rounded-full ${meta.dot}`} aria-hidden />
        <span className="text-[var(--text-secondary)]">{meta.label}</span>
        {days !== null && days >= 0 && (
          <span className="text-[var(--text-dim)] inline-flex items-center gap-1">
            <Clock className="w-3 h-3" strokeWidth={1.4} /> {days}T
          </span>
        )}
        <ChevronDown className={`w-3 h-3 text-[var(--text-dim)] transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute left-0 mt-1 z-50 min-w-[220px] py-2 bg-[#1A1612] border border-white/[0.06] rounded-sm"
            style={{ boxShadow: "0 24px 60px -20px rgba(0,0,0,0.6)" }}
          >
            <div className="px-4 py-1.5 text-[9px] uppercase tracking-[0.22em] text-[var(--text-dim)] border-b border-white/5 mb-1">
              Aktueller Status: {meta.label}
            </div>
            {items.map((it) => (
              <button
                key={it.key}
                onClick={() => { setOpen(false); onAction(rest.restaurant_id, it.key); }}
                data-testid={`billing-action-${rest.restaurant_id}-${it.key}`}
                className={`w-full text-left px-4 py-2 text-[11px] tracking-[0.06em] transition-colors ${
                  it.tone === "primary"
                    ? "text-[var(--gold)] hover:bg-gold/10"
                    : it.tone === "danger"
                    ? "text-[#C98577] hover:bg-[#C98577]/10"
                    : "text-[var(--text-secondary)] hover:bg-white/[0.04] hover:text-[var(--gold)]"
                }`}
              >
                {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Admin() {
  const { user, logout, impersonate } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [expanded, setExpanded] = useState({}); // restaurant_id -> bool
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [statsView, setStatsView] = useState(null); // {loading, data, restaurant}
  const [notesView, setNotesView] = useState(null); // {restaurant_id, restaurant_name}
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    restaurant_name: "", slug: "", tagline: "", address: "",
    owner_name: "", owner_email: "", owner_password: "",
  });

  const load = async () => {
    const [s, r] = await Promise.all([api.get("/admin/stats"), api.get("/admin/restaurants")]);
    setStats(s.data);
    setRestaurants(r.data);
  };
  useEffect(() => { load(); }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("admin.delete_confirm"))) return;
    await api.delete(`/admin/restaurants/${id}`);
    toast.success("Gelöscht");
    load();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/restaurants", form);
      toast.success("Restaurant erstellt");
      setShowCreate(false);
      setForm({ restaurant_name: "", slug: "", tagline: "", address: "", owner_name: "", owner_email: "", owner_password: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setCreating(false);
    }
  };

  const handleImpersonate = async (userId, userName) => {
    if (!userId) {
      toast.error("Kein Benutzer verknüpft");
      return;
    }
    try {
      await impersonate(userId);
      toast.success(`Sie agieren jetzt als ${userName}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impersonation fehlgeschlagen");
    }
  };

  const handleAnalytics = async (restaurantId, restaurantName) => {
    setStatsView({ loading: true, data: null, restaurant: restaurantName });
    try {
      const { data } = await api.get(`/admin/restaurants/${restaurantId}/stats`);
      setStatsView({ loading: false, data, restaurant: restaurantName });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Statistiken konnten nicht geladen werden");
      setStatsView(null);
    }
  };

  const handleBilling = async (restaurantId, action) => {
    try {
      const body = { action };
      if (action === "extend_trial") body.days = 30;
      const { data } = await api.post(`/admin/restaurants/${restaurantId}/billing`, body);
      const label = {
        extend_trial: "Testphase verlängert (+30 Tage)",
        activate: "Restaurant aktiviert",
        mark_payment_open: "Status: Zahlung offen",
        mark_paid: "Als bezahlt markiert",
        suspend: "Restaurant pausiert",
        block: "Restaurant blockiert",
        reactivate: "Restaurant reaktiviert",
      }[action] || "Status aktualisiert";
      toast.success(label);
      setRestaurants((curr) => curr.map((r) => (r.restaurant_id === restaurantId ? { ...r, ...data } : r)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const filteredRestaurants = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return restaurants;
    return restaurants.filter((r) => {
      const haystack = [
        r.name,
        r.slug,
        r.address,
        r.tagline,
        r.owner_name,
        r.owner_email,
        r.phone,
        ...(r.team || []).flatMap((m) => [m.name, m.email, m.phone]),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  })();

  const ROLE_STYLES = {
    owner:     "bg-gold/15 text-gold border-gold/30",
    manager:   "bg-blue-500/10 text-blue-300 border-blue-500/20",
    host:      "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
    waiter:    "bg-white/5 text-[var(--text-primary)] border-white/10",
    bartender: "bg-purple-500/10 text-purple-300 border-purple-500/20",
  };
  const ROLE_LABEL = {
    owner: "Inhaber", manager: "Manager", host: "Host", waiter: "Kellner", bartender: "Barkeeper",
  };

  return (
    <div className="min-h-screen bg-[var(--bg-rooftop)] text-[var(--text-primary)]">
      <div className="relative border-b border-white/[0.04] glass">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[68px] flex items-center justify-between">
          <Link to="/admin" className="block group" data-testid="admin-logo">
            <span className="font-heading font-extralight text-xl tracking-[0.55em] text-[var(--text-primary)] group-hover:text-gold transition-colors duration-500">
              SONARA
            </span>
            <span className="block text-[10px] tracking-[0.45em] text-gold/70 mt-0.5 uppercase">Admin</span>
          </Link>
          <div className="flex items-center gap-5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-right">
              <div className="text-[var(--text-primary)]">{user?.name}</div>
              <div className="text-[var(--text-dim)] mt-0.5">{user?.email}</div>
            </div>
            <button onClick={handleLogout} className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] hover:text-gold transition-colors inline-flex items-center gap-2" data-testid="admin-logout">
              <LogOut className="w-3.5 h-3.5" /> {t("auth.logout")}
            </button>
          </div>
        </div>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <header className="mb-12 animate-fade-up">
          <h1 className="font-editorial italic text-5xl sm:text-6xl leading-[1.05] tracking-tight text-[var(--text-primary)]" data-testid="admin-title">{t("admin.title")}</h1>
        </header>

        {stats && (
          <div className="mb-10">
            <div data-testid="admin-stat-restaurants" className="surface-1 rounded-md p-8 max-w-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                  {t("admin.total_restaurants")}
                </div>
                <Building2 className="w-4 h-4 text-gold" strokeWidth={1.5} />
              </div>
              <div className="font-heading text-5xl font-extralight text-gold leading-none">
                {stats.total_restaurants}
              </div>
            </div>
          </div>
        )}

        {/* Suche — lange, prominente Leiste */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-dim)]" strokeWidth={1.5} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Restaurant, Inhaber, E-Mail oder Telefon suchen…"
              data-testid="admin-search"
              className="w-full bg-[#141414] border border-white/10 rounded-md pl-12 pr-12 h-14 text-sm text-[var(--text-primary)] placeholder-[var(--text-dim)] focus:border-gold/40 outline-none transition-colors"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                data-testid="admin-search-clear"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-dim)] hover:text-gold p-1 transition-colors"
                title="Suche löschen"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>

        <section className="surface-1 rounded-md">
          <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
            <h2 className="font-heading text-lg font-normal">{t("admin.restaurants")}</h2>
            <div className="flex items-center gap-4">
              <span className="text-xs text-[var(--text-dim)]">
                {search ? `${filteredRestaurants.length} / ${restaurants.length}` : `${restaurants.length} gesamt`}
              </span>
              <button
                onClick={() => setShowCreate(true)}
                data-testid="admin-create-restaurant-btn"
                className="btn-primary text-xs inline-flex items-center gap-2"
              >
                <Plus className="w-3.5 h-3.5" /> Restaurant erstellen
              </button>
            </div>
          </div>
          <div className="hidden md:grid grid-cols-12 gap-x-6 px-6 py-3 text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] bg-white/[0.02]">
            <div className="col-span-3">{t("common.name")}</div>
            <div className="col-span-2">Adresse</div>
            <div className="col-span-3 pl-4">{t("admin.owner")}</div>
            <div className="col-span-1 text-center">{t("nav.staff")}</div>
            <div className="col-span-2">Abo-Status</div>
            <div className="col-span-1 text-right">{t("common.actions")}</div>
          </div>
          {filteredRestaurants.length === 0 && (
            <div className="p-12 text-center text-sm text-[var(--text-dim)]">
              {search ? `Keine Treffer für „${search}".` : "Noch keine Restaurants."}
            </div>
          )}
          {filteredRestaurants.map((r) => (
            <div key={r.restaurant_id} data-testid={`admin-restaurant-${r.restaurant_id}`} className="border-t border-white/5">
              <div className="grid grid-cols-2 md:grid-cols-12 gap-x-6 gap-y-2 px-6 py-4 items-center hover:bg-white/[0.02] transition-colors">
                <div className="md:col-span-3 flex items-center gap-2">
                  <button
                    onClick={() => toggleExpand(r.restaurant_id)}
                    data-testid={`admin-expand-${r.restaurant_id}`}
                    className="text-[var(--text-dim)] hover:text-gold transition-colors p-0.5"
                    aria-label="Team anzeigen"
                  >
                    {expanded[r.restaurant_id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <div>
                    <div className="text-sm font-medium">{r.name}</div>
                    <div className="text-[10px] text-gold/70 font-mono">{r.slug}</div>
                  </div>
                </div>
                <div className="md:col-span-2 text-xs text-[var(--text-muted)] flex items-start gap-1.5">
                  {r.address ? (
                    <>
                      <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-[var(--text-dim)]" strokeWidth={1.5} />
                      <span className="leading-snug">{r.address}</span>
                    </>
                  ) : (
                    <span className="text-[var(--text-faint)] italic">—</span>
                  )}
                </div>
                <div className="md:col-span-3 text-xs text-[var(--text-muted)] min-w-0 pl-4">
                  <div className="truncate">{r.owner_name}</div>
                  <div className="text-[var(--text-dim)] truncate">{r.owner_email}</div>
                </div>
                <div className="md:col-span-1 text-center text-sm">
                  <button
                    onClick={() => toggleExpand(r.restaurant_id)}
                    className="text-[var(--text-primary)] hover:text-gold transition-colors"
                    data-testid={`admin-team-count-${r.restaurant_id}`}
                  >
                    {r.staff_count}
                  </button>
                </div>
                <div className="md:col-span-2">
                  <SubscriptionControl rest={r} onAction={handleBilling} />
                </div>
                <div className="md:col-span-1 flex justify-end items-center gap-0.5 border-l border-white/5 pl-3 ml-2">
                  <button
                    onClick={() => handleImpersonate(r.owner_id, r.owner_name)}
                    title={`Als ${r.owner_name || "Inhaber"} einloggen`}
                    data-testid={`admin-impersonate-${r.restaurant_id}`}
                    className="text-[var(--text-muted)] hover:text-gold p-1.5 transition-colors hover:bg-gold/5 rounded"
                  >
                    <LogIn className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <a
                    href={`/r/${r.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Buchungsmaske öffnen"
                    className="text-[var(--text-muted)] hover:text-gold p-1.5 transition-colors hover:bg-gold/5 rounded"
                    data-testid={`admin-view-${r.restaurant_id}`}
                  >
                    <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                  <button
                    onClick={() => handleAnalytics(r.restaurant_id, r.name)}
                    title="Statistiken einsehen"
                    data-testid={`admin-analytics-${r.restaurant_id}`}
                    className="text-[var(--text-muted)] hover:text-gold p-1.5 transition-colors hover:bg-gold/5 rounded"
                  >
                    <BarChart3 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={() => setNotesView({ restaurant_id: r.restaurant_id, restaurant_name: r.name })}
                    title="Notizen verwalten"
                    data-testid={`admin-notes-${r.restaurant_id}`}
                    className="text-[var(--text-muted)] hover:text-gold p-1.5 transition-colors hover:bg-gold/5 rounded"
                  >
                    <StickyNote className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={() => handleDelete(r.restaurant_id)}
                    title="Restaurant löschen"
                    className="text-[var(--text-dim)] hover:text-red-400 p-1.5 transition-colors"
                    data-testid={`admin-delete-${r.restaurant_id}`}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              {/* Team panel */}
              {expanded[r.restaurant_id] && (
                <div className="px-6 pb-5 pt-1 bg-[var(--bg-rooftop)]/60" data-testid={`admin-team-${r.restaurant_id}`}>
                  <div className="ml-6 border-l border-white/5 pl-6 py-2">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {r.team.map((m) => (
                        <div
                          key={m.user_id}
                          data-testid={`admin-team-member-${m.user_id}`}
                          className="surface-2 rounded-md px-4 py-3 flex items-center gap-3 group hover:border-gold/30 transition-all"
                        >
                          <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-xs flex-shrink-0">
                            {m.name?.[0]?.toUpperCase() || "U"}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{m.name}</div>
                            <div className="text-[10px] text-[var(--text-dim)] truncate">{m.email}</div>
                          </div>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border whitespace-nowrap ${ROLE_STYLES[m.role] || "bg-white/5 text-[var(--text-muted)] border-white/10"}`}>
                            {ROLE_LABEL[m.role] || m.role}
                          </span>
                          <button
                            onClick={() => handleImpersonate(m.user_id, m.name)}
                            data-testid={`admin-impersonate-user-${m.user_id}`}
                            title={`Als ${m.name} einloggen`}
                            className="text-[var(--text-dim)] hover:text-gold p-1.5 transition-colors hover:bg-gold/5 rounded opacity-0 group-hover:opacity-100"
                          >
                            <LogIn className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </button>
                        </div>
                      ))}
                      {r.team.length === 0 && (
                        <div className="text-sm text-[var(--text-dim)] py-4">Noch keine Teammitglieder.</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </section>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowCreate(false); }}>
          <div className="surface-1 rounded-md w-full max-w-xl p-8 my-12" onMouseDown={(e) => e.stopPropagation()}>
            <h3 className="font-editorial italic text-3xl leading-tight mb-6 text-[var(--text-primary)]">Restaurant & Inhaber anlegen</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Restaurantname">
                  <input required value={form.restaurant_name} onChange={(e) => setForm({ ...form, restaurant_name: e.target.value })} data-testid="admin-new-name" className="input" placeholder="Aurora Rooftop" />
                </Field>
                <Field label="Slug (URL)">
                  <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="admin-new-slug" className="input" placeholder="aurora-rooftop" />
                </Field>
              </div>
              <Field label="Tagline">
                <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="admin-new-tagline" className="input" placeholder="Deep house. Sunset views." />
              </Field>
              <Field label="Adresse">
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="admin-new-address" className="input" placeholder="Bahnhofstrasse 1, 8001 Zürich" />
              </Field>
              <div className="border-t border-white/5 pt-4">
                <div className="space-y-4">
                  <Field label="Name des Inhabers">
                    <input required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="admin-new-owner-name" className="input" />
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="E-Mail">
                      <input type="email" required value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} data-testid="admin-new-owner-email" className="input" />
                    </Field>
                    <Field label="Passwort">
                      <input type="text" required minLength={6} value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} data-testid="admin-new-owner-password" className="input" />
                    </Field>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-ghost text-xs flex-1">Abbrechen</button>
                <button type="submit" disabled={creating} data-testid="admin-new-submit" className="btn-primary text-xs flex-1 disabled:opacity-50">
                  {creating ? "Erstelle…" : "Erstellen"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {statsView && (
        <RestaurantStatsModal view={statsView} onClose={() => setStatsView(null)} />
      )}

      {notesView && (
        <RestaurantNotesModal
          restaurantId={notesView.restaurant_id}
          restaurantName={notesView.restaurant_name}
          onClose={() => setNotesView(null)}
        />
      )}

      <style>{`.input{width:100%;background:#141414;border:1px solid rgba(255,255,255,0.1);border-radius:6px;padding:0 16px;height:44px;font-size:14px;color:#F5F5F0;outline:none;}.input:focus{border-color:#D4AF37;}`}</style>
    </div>
  );
}

const STATUS_LABEL = {
  trial: "Testphase",
  trial_expired: "Testphase abgelaufen",
  active: "Aktiv",
  payment_open: "Zahlung offen",
  suspended: "Pausiert",
  blocked: "Blockiert",
};

const RES_STATUS_LABEL = {
  confirmed: "Bestätigt",
  pending: "Ausstehend",
  cancelled: "Storniert",
  no_show: "No-Show",
  seated: "Eingecheckt",
  completed: "Abgeschlossen",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function StatTile({ label, value, sub }) {
  return (
    <div className="surface-2 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] mb-2">{label}</div>
      <div className="font-heading text-3xl font-extralight text-gold leading-none">{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-[var(--text-dim)] mt-1.5">{sub}</div>}
    </div>
  );
}

function RestaurantStatsModal({ view, onClose }) {
  const data = view.data;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/85 backdrop-blur-sm p-6 overflow-y-auto"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="admin-stats-modal"
    >
      <div
        className="surface-1 rounded-md w-full max-w-5xl my-8 border border-white/[0.06]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="px-8 py-6 border-b border-white/5 flex items-start justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-gold/70 mb-1">Statistiken</div>
            <h3 className="font-editorial italic text-3xl text-[var(--text-primary)] leading-tight">
              {view.restaurant}
            </h3>
            {data?.restaurant?.address && (
              <div className="text-xs text-[var(--text-muted)] mt-2 flex items-center gap-1.5">
                <MapPin className="w-3 h-3" strokeWidth={1.5} /> {data.restaurant.address}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="admin-stats-close"
            className="text-[var(--text-muted)] hover:text-gold transition-colors text-[10px] uppercase tracking-[0.22em]"
          >
            Schliessen ✕
          </button>
        </div>

        {view.loading || !data ? (
          <div className="p-16 text-center text-sm text-[var(--text-dim)]">Lade Daten…</div>
        ) : (
          <div className="p-8 space-y-10">
            {/* Reservierungen Übersicht */}
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)] mb-4">Reservierungen</h4>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <StatTile label="Gesamt"        value={data.reservations.total} />
                <StatTile label="Heute"         value={data.reservations.today} />
                <StatTile label="Diese Woche"   value={data.reservations.week} />
                <StatTile label="Diesen Monat"  value={data.reservations.month} />
                <StatTile label="Nächste 30 T." value={data.reservations.upcoming_30d} />
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Gäste-Metriken */}
              <div className="surface-2 rounded-md p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] mb-4">Gäste</div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-[var(--text-muted)]">Gäste gesamt</span><span className="text-[var(--text-primary)]">{data.reservations.total_guests}</span></div>
                  <div className="flex justify-between"><span className="text-[var(--text-muted)]">Ø Gruppengrösse</span><span className="text-[var(--text-primary)]">{data.reservations.avg_party_size}</span></div>
                </div>
              </div>

              {/* By Status */}
              <div className="surface-2 rounded-md p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] mb-4">Nach Status</div>
                <div className="space-y-2 text-sm">
                  {Object.entries(data.reservations.by_status).length === 0 && (
                    <div className="text-[var(--text-dim)] text-xs italic">Keine Daten</div>
                  )}
                  {Object.entries(data.reservations.by_status).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-[var(--text-muted)]">{RES_STATUS_LABEL[k] || k}</span>
                      <span className="text-[var(--text-primary)]">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* By Source */}
              <div className="surface-2 rounded-md p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] mb-4">Nach Quelle</div>
                <div className="space-y-2 text-sm">
                  {Object.entries(data.reservations.by_source).length === 0 && (
                    <div className="text-[var(--text-dim)] text-xs italic">Keine Daten</div>
                  )}
                  {Object.entries(data.reservations.by_source).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-[var(--text-muted)]">{k === "online" ? "Online" : k === "walk_in" ? "Walk-in" : k === "phone" ? "Telefon" : k}</span>
                      <span className="text-[var(--text-primary)]">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Infrastruktur + Team */}
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="surface-2 rounded-md p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] mb-4">Infrastruktur</div>
                <div className="grid grid-cols-3 gap-3">
                  <StatTile label="Räume"   value={data.infrastructure.rooms} />
                  <StatTile label="Tische"  value={data.infrastructure.tables} />
                  <StatTile label="Services" value={data.infrastructure.services} />
                </div>
              </div>
              <div className="surface-2 rounded-md p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] mb-4">Team — {data.team.total} Mitglieder</div>
                <div className="space-y-1.5 text-sm">
                  {Object.entries(data.team.by_role).map(([role, count]) => (
                    <div key={role} className="flex justify-between">
                      <span className="text-[var(--text-muted)] capitalize">{role}</span>
                      <span className="text-[var(--text-primary)]">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Inhaber + Abo */}
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="surface-2 rounded-md p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] mb-4">Inhaber</div>
                {data.owner ? (
                  <div className="space-y-1.5 text-sm">
                    <div className="text-[var(--text-primary)]">{data.owner.name}</div>
                    <div className="text-[var(--text-muted)]">{data.owner.email}</div>
                    <div className="text-[10px] text-[var(--text-dim)] mt-3">Erstellt am {fmtDate(data.owner.created_at)}</div>
                  </div>
                ) : (
                  <div className="text-sm text-[var(--text-dim)] italic">Kein Inhaber zugeordnet</div>
                )}
              </div>
              <div className="surface-2 rounded-md p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] mb-4">Abonnement</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[var(--text-muted)]">Status</span><span className="text-gold">{STATUS_LABEL[data.restaurant.subscription_status] || data.restaurant.subscription_status}</span></div>
                  <div className="flex justify-between"><span className="text-[var(--text-muted)]">Restaurant seit</span><span>{fmtDate(data.restaurant.created_at)}</span></div>
                  {data.restaurant.trial_started_at && (
                    <div className="flex justify-between"><span className="text-[var(--text-muted)]">Testphase Start</span><span>{fmtDate(data.restaurant.trial_started_at)}</span></div>
                  )}
                  {data.restaurant.trial_ends_at && (
                    <div className="flex justify-between"><span className="text-[var(--text-muted)]">Testphase Ende</span><span>{fmtDate(data.restaurant.trial_ends_at)}</span></div>
                  )}
                  {data.restaurant.activated_at && (
                    <div className="flex justify-between"><span className="text-[var(--text-muted)]">Aktiviert am</span><span>{fmtDate(data.restaurant.activated_at)}</span></div>
                  )}
                  {data.restaurant.suspended_at && (
                    <div className="flex justify-between"><span className="text-[var(--text-muted)]">Pausiert am</span><span>{fmtDate(data.restaurant.suspended_at)}</span></div>
                  )}
                  {data.restaurant.last_billing_note && (
                    <div className="pt-2 border-t border-white/5 mt-2 text-xs text-[var(--text-dim)] italic">„{data.restaurant.last_billing_note}"</div>
                  )}
                </div>
              </div>
            </div>

            {/* Nächste Ankünfte */}
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)] mb-4">Nächste Ankünfte</h4>
              <div className="surface-2 rounded-md divide-y divide-white/[0.04]">
                {data.next_arrivals.length === 0 && (
                  <div className="p-5 text-sm text-[var(--text-dim)] italic">Keine bevorstehenden Reservierungen.</div>
                )}
                {data.next_arrivals.map((res, i) => (
                  <div key={res.reservation_id || i} className="px-5 py-3 flex items-center gap-4 text-sm">
                    <div className="text-gold font-mono text-xs w-24">{res.date} · {res.time}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[var(--text-primary)] truncate">{res.guest_name}</div>
                      <div className="text-[var(--text-dim)] text-xs truncate">{res.guest_email || res.guest_phone || "—"}</div>
                    </div>
                    <div className="text-[var(--text-muted)] text-xs">{res.party_size} Gäste</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Letzte Aktivität */}
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)] mb-4">Letzte Aktivität</h4>
              <div className="surface-2 rounded-md divide-y divide-white/[0.04]">
                {data.recent.length === 0 && (
                  <div className="p-5 text-sm text-[var(--text-dim)] italic">Noch keine Aktivität.</div>
                )}
                {data.recent.map((res, i) => (
                  <div key={res.reservation_id || i} className="px-5 py-3 flex items-center gap-4 text-sm">
                    <div className="text-[var(--text-dim)] font-mono text-[10px] w-32">{fmtDate(res.created_at)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[var(--text-primary)] truncate">{res.guest_name} <span className="text-[var(--text-dim)]">·</span> <span className="text-[var(--text-muted)] text-xs">{res.date} {res.time}</span></div>
                    </div>
                    <div className="text-[var(--text-muted)] text-xs">{RES_STATUS_LABEL[res.status] || res.status}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-[var(--text-muted)] mb-2 block uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
    const time = d.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" });
    return { date, time };
  } catch {
    return { date: iso, time: "" };
  }
}

function RestaurantNotesModal({ restaurantId, restaurantName, onClose }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [authorName, setAuthorName] = useState(() => localStorage.getItem("sonara_admin_note_author") || "");
  const [attachments, setAttachments] = useState([]); // {name, mime, size, data_url}
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null); // data_url for preview
  const fileInputRef = React.useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/restaurants/${restaurantId}/notes`);
      setNotes(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Notizen konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [restaurantId]);

  const handleFiles = (files) => {
    const arr = Array.from(files);
    arr.forEach((file) => {
      if (file.size > 4 * 1024 * 1024) {
        toast.error(`„${file.name}" überschreitet 4 MB.`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setAttachments((cur) => [...cur, {
          name: file.name,
          mime: file.type || "",
          size: file.size,
          data_url: reader.result,
        }]);
      };
      reader.onerror = () => toast.error(`„${file.name}" konnte nicht gelesen werden.`);
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (idx) => setAttachments((cur) => cur.filter((_, i) => i !== idx));

  const add = async (e) => {
    e.preventDefault();
    const trimmedAuthor = authorName.trim();
    if (!trimmedAuthor) {
      toast.error("Bitte Ihren Namen angeben.");
      return;
    }
    if (!text.trim() && attachments.length === 0) {
      toast.error("Bitte einen Text oder Anhang hinzufügen.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/restaurants/${restaurantId}/notes`, {
        text,
        author_name: trimmedAuthor,
        attachments,
      });
      setNotes((n) => [data, ...n]);
      setText("");
      setAttachments([]);
      localStorage.setItem("sonara_admin_note_author", trimmedAuthor);
      toast.success("Notiz gespeichert");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (noteId) => {
    if (!window.confirm("Notiz wirklich löschen?")) return;
    try {
      await api.delete(`/admin/restaurants/${restaurantId}/notes/${noteId}`);
      setNotes((n) => n.filter((x) => x.note_id !== noteId));
      toast.success("Notiz gelöscht");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const fmtSize = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/85 backdrop-blur-sm p-6 overflow-y-auto"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="admin-notes-modal"
    >
      <div
        className="surface-1 rounded-md w-full max-w-3xl my-8 border border-white/[0.06]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="px-8 py-6 border-b border-white/5 flex items-start justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-gold/70 mb-1">Notizen</div>
            <h3 className="font-editorial italic text-3xl text-[var(--text-primary)] leading-tight">
              {restaurantName}
            </h3>
            <div className="text-xs text-[var(--text-dim)] mt-2">
              {notes.length} Eintrag{notes.length === 1 ? "" : "e"}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-notes-close"
            className="text-[var(--text-muted)] hover:text-gold transition-colors p-1"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-8 space-y-6">
          <form onSubmit={add} className="space-y-4">
            <label className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] block">
              Neue Notiz
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="admin-note-text"
              placeholder="z.B. Telefongespräch mit Inhaber — Zahlung bis Freitag zugesagt."
              rows={4}
              className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-sm text-[var(--text-primary)] placeholder-[var(--text-faint)] focus:border-gold/50 outline-none resize-none"
            />

            {/* Anhänge */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 bg-[#141414] border border-white/10 rounded px-3 py-2 text-xs">
                    {a.mime.startsWith("image/") ? (
                      <img src={a.data_url} alt={a.name} className="w-10 h-10 object-cover rounded border border-white/10" />
                    ) : (
                      <FileText className="w-5 h-5 text-gold/70" strokeWidth={1.5} />
                    )}
                    <div className="min-w-0">
                      <div className="text-[var(--text-primary)] truncate max-w-[160px]">{a.name}</div>
                      <div className="text-[var(--text-dim)] text-[10px]">{fmtSize(a.size)}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(i)}
                      className="text-[var(--text-dim)] hover:text-red-400 p-1"
                      title="Entfernen"
                    >
                      <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,application/pdf,.doc,.docx,.txt,.csv,.xlsx"
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
              className="hidden"
              data-testid="admin-note-file-input"
            />

            <div className="flex flex-col sm:flex-row sm:items-end gap-3 pt-2">
              <div className="flex-1">
                <label className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] block mb-2">
                  Ihr Name <span className="text-[#C98577]">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  placeholder="z.B. Massimo G."
                  data-testid="admin-note-author"
                  className="w-full bg-[#141414] border border-white/10 rounded-md px-4 h-11 text-sm text-[var(--text-primary)] placeholder-[var(--text-faint)] focus:border-gold/50 outline-none"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="admin-note-attach"
                  title="Datei oder Foto hinzufügen"
                  className="btn-ghost text-xs inline-flex items-center gap-2 h-11"
                >
                  <Paperclip className="w-4 h-4" strokeWidth={1.5} /> Anhang
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="admin-note-submit"
                  className="btn-primary text-xs inline-flex items-center gap-2 h-11 disabled:opacity-40"
                >
                  {saving ? "Speichern…" : "Notiz hinzufügen"}
                </button>
              </div>
            </div>
          </form>

          <div className="border-t border-white/5 pt-6">
            <label className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] block mb-4">
              Verlauf
            </label>
            {loading ? (
              <div className="text-sm text-[var(--text-dim)] text-center py-8">Lade Notizen…</div>
            ) : notes.length === 0 ? (
              <div className="text-sm text-[var(--text-dim)] italic text-center py-8">
                Noch keine Notizen vorhanden.
              </div>
            ) : (
              <div className="space-y-2">
                {notes.map((n) => {
                  const dt = fmtDateTime(n.created_at);
                  const atts = n.attachments || [];
                  return (
                    <div
                      key={n.note_id}
                      data-testid={`admin-note-${n.note_id}`}
                      className="surface-2 rounded-md px-4 py-3 flex items-start gap-4 group"
                    >
                      <div className="text-[10px] uppercase tracking-[0.18em] text-gold/70 font-mono whitespace-nowrap pt-0.5 w-28">
                        <div>{dt.date}</div>
                        <div className="text-[var(--text-dim)] mt-0.5">{dt.time}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        {n.text && (
                          <div className="text-sm text-[var(--text-primary)] whitespace-pre-wrap break-words leading-relaxed">
                            {n.text}
                          </div>
                        )}
                        {atts.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {atts.map((a, i) => (
                              a.mime?.startsWith("image/") ? (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => setLightbox(a)}
                                  className="block group/img"
                                  data-testid={`note-attachment-img-${i}`}
                                >
                                  <img
                                    src={a.data_url}
                                    alt={a.name}
                                    className="w-24 h-24 object-cover rounded border border-white/10 group-hover/img:border-gold/40 transition-colors"
                                  />
                                </button>
                              ) : (
                                <a
                                  key={i}
                                  href={a.data_url}
                                  download={a.name}
                                  data-testid={`note-attachment-file-${i}`}
                                  className="flex items-center gap-2 bg-[#141414] border border-white/10 hover:border-gold/30 rounded px-3 py-2 text-xs text-[var(--text-secondary)] hover:text-gold transition-colors"
                                >
                                  <FileText className="w-4 h-4 text-gold/70" strokeWidth={1.5} />
                                  <span className="truncate max-w-[200px]">{a.name}</span>
                                  <span className="text-[var(--text-dim)] text-[10px]">{fmtSize(a.size || 0)}</span>
                                </a>
                              )
                            ))}
                          </div>
                        )}
                        <div className="text-[10px] text-[var(--text-dim)] mt-2 uppercase tracking-[0.14em]">
                          Erstellt von <span className="text-[var(--text-muted)]">{n.created_by_name}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => remove(n.note_id)}
                        title="Notiz löschen"
                        data-testid={`admin-note-delete-${n.note_id}`}
                        className="text-[var(--text-dim)] hover:text-red-400 p-1.5 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-8"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setLightbox(null); }}
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 text-white/80 hover:text-gold p-2"
            data-testid="note-lightbox-close"
          >
            <X className="w-6 h-6" strokeWidth={1.5} />
          </button>
          <img src={lightbox.data_url} alt={lightbox.name} className="max-w-full max-h-full object-contain" />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-white/60 uppercase tracking-[0.18em]">
            {lightbox.name}
          </div>
        </div>
      )}
    </div>
  );
}
